import React, { useState } from 'react';
import { Modal } from 'react-bootstrap';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { AiOutlineDownload, AiOutlinePrinter } from 'react-icons/ai';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './CustomerFilterDetailsModal.css';

export default function CustomerFilterDetailsModal({ show, onHide, customerFilter }) {
  const [showFileNamePrompt, setShowFileNamePrompt] = useState(false);
  const [pendingExportType, setPendingExportType] = useState(null); // 'pdf' or 'print'
  const [customFileName, setCustomFileName] = useState('');

  if (!show || !customerFilter) return null;

  // Normalize fields
  const id = customerFilter.id ?? customerFilter.ID ?? '';
  const customerName = customerFilter.customerName ?? customerFilter['Customer Name'] ?? 
    (customerFilter.Customer ? `${customerFilter.Customer.firstName} ${customerFilter.Customer.lastName}` : '') ?? '';
  const filterName = customerFilter.filterName ?? customerFilter['Filter Name'] ?? 
    (customerFilter.Filter ? customerFilter.Filter.filter_name : '') ?? '';
  const dateOfInstallation = customerFilter.date_of_installation ?? customerFilter['Date of Installation'] ?? '';
  const dateToBeChanged = customerFilter.date_to_be_changed ?? customerFilter['Date to be Changed'] ?? '';

  // File name prompt logic
  const handleExportWithPrompt = (type) => {
    setPendingExportType(type);
    setCustomFileName('');
    setShowFileNamePrompt(true);
  };

  const handleFileNameSubmit = (e) => {
    e.preventDefault();
    let name = customFileName.trim();
    if (!name) name = `customer_filter_${id}`;
    if (pendingExportType === 'pdf' && !name.endsWith('.pdf')) name += '.pdf';
    setShowFileNamePrompt(false);
    if (pendingExportType === 'pdf') handleSavePDF(name);
    if (pendingExportType === 'print') handlePrint(name);
  };

  // PDF export
  const handleSavePDF = (customName) => {
    try {
      const doc = new jsPDF({ unit: 'pt', format: 'a4', orientation: 'portrait' });
      const margin = 40;
      const pageWidth = doc.internal.pageSize.getWidth();
      doc.setFontSize(20);
      doc.setTextColor(16, 39, 123);
      doc.text('Customer Filter Details', margin, margin);
      const rows = [
        ['ID', id],
        ['Customer Name', customerName],
        ['Filter Name', filterName],
        ['Date of Installation', dateOfInstallation ? new Date(dateOfInstallation).toLocaleDateString() : ''],
        ['Date to be Changed', dateToBeChanged ? new Date(dateToBeChanged).toLocaleDateString() : '']
      ];
      autoTable(doc, {
        startY: margin + 30,
        margin: { left: margin, right: margin },
        head: [['Field', 'Value']],
        body: rows,
        theme: 'grid',
        styles: { fontSize: 12, cellPadding: 6 },
        headStyles: { fillColor: [16, 39, 123], textColor: 255 },
        columnStyles: { 0: { cellWidth: 140 }, 1: { cellWidth: pageWidth - margin * 2 - 140 } }
      });
      doc.save(customName);
      toast.success('PDF saved successfully!', { autoClose: 2000 });
    } catch (error) {
      toast.error('Error generating PDF. Please try again.', { autoClose: 3000 });
    }
  };

  // Print export
  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head>
            <title>Customer Filter Details</title>
            <style>
              body { font-family: 'Inter', Arial, sans-serif; margin: 40px; color: #10277B; }
              h2 { color: #10277B; }
              table { width: 100%; border-collapse: collapse; margin-top: 20px; }
              th, td { border: 1px solid #10277B; padding: 10px; text-align: left; font-size: 16px; }
              th { background: #10277B; color: #fff; }
              tr:nth-child(even) { background: #f5f5f5; }
            </style>
          </head>
          <body>
            <h2>Customer Filter Details</h2>
            <table>
              <tr><th>Field</th><th>Value</th></tr>
              <tr><td>ID</td><td>${id}</td></tr>
              <tr><td>Customer Name</td><td>${customerName}</td></tr>
              <tr><td>Filter Name</td><td>${filterName}</td></tr>
              <tr><td>Date of Installation</td><td>${dateOfInstallation ? new Date(dateOfInstallation).toLocaleDateString() : ''}</td></tr>
              <tr><td>Date to be Changed</td><td>${dateToBeChanged ? new Date(dateToBeChanged).toLocaleDateString() : ''}</td></tr>
            </table>
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
      printWindow.close();
      toast.success('Print dialog opened!', { autoClose: 2000 });
    } else {
      toast.error('Unable to open print dialog.', { autoClose: 3000 });
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="md" centered backdrop="static">
      <Modal.Header className="modal-header-custom">
        <Modal.Title className="modal-title-custom">Customer Filter Details</Modal.Title>
        <button type="button" className="btn-close" onClick={onHide} />
      </Modal.Header>
      <div className="modal-actions-row right-actions">
        <AiOutlineDownload className="action-icon" title="Download PDF" onClick={() => handleExportWithPrompt('pdf')} />
        <AiOutlinePrinter className="action-icon" title="Print" onClick={() => handleExportWithPrompt('print')} />
      </div>
      <Modal.Body className="modal-body-custom">
        <div className="customer-details-card">
          <div className="details-row">
            <div className="details-label">ID:</div>
            <div className="details-value">{id}</div>
          </div>
          <div className="details-row">
            <div className="details-label">Customer Name:</div>
            <div className="details-value">{customerName}</div>
          </div>
          <div className="details-row">
            <div className="details-label">Filter Name:</div>
            <div className="details-value">{filterName}</div>
          </div>
          <div className="details-row">
            <div className="details-label">Date of Installation:</div>
            <div className="details-value">{dateOfInstallation ? new Date(dateOfInstallation).toLocaleDateString() : 'N/A'}</div>
          </div>
          <div className="details-row">
            <div className="details-label">Date to be Changed:</div>
            <div className="details-value">{dateToBeChanged ? new Date(dateToBeChanged).toLocaleDateString() : 'N/A'}</div>
          </div>
        </div>
      </Modal.Body>
      {/* File name prompt modal */}
      {showFileNamePrompt && (
        <div className="file-name-prompt-backdrop">
          <div className="file-name-prompt-modal pretty-modal">
            <form onSubmit={handleFileNameSubmit}>
              <label htmlFor="exportFileName" className="pretty-label">Enter file name:</label>
              <input
                id="exportFileName"
                type="text"
                value={customFileName}
                onChange={e => setCustomFileName(e.target.value)}
                autoFocus
                placeholder={pendingExportType === 'pdf' ? `customer_filter_${id}.pdf` : `customer_filter_${id}`}
                className="file-name-input pretty-input"
              />
              <div className="file-name-prompt-actions pretty-actions">
                <button type="submit" className="btn-save pretty-save">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}
      <ToastContainer
        position="top-center"
        autoClose={2000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
      />
    </Modal>
  );
}
